import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "backdrop", "modal", "searchInput", "results", "quickActions", "navigationItems", "helpItems", "actionItem"]
  static values = { 
    allowBackgroundClose: Boolean, 
    closeOnEsc: Boolean 
  }

  connect() {
    this.isOpen = false
    this.selectedIndex = -1
    this.filteredItems = []
    
    // Bind the global keydown handler to preserve context
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    
    // Keyboard shortcut listener
    document.addEventListener('keydown', this.boundHandleGlobalKeydown)
    
    // Listen for quick access events
    this.boundHandleQuickAccessOpen = this.handleQuickAccessOpen.bind(this)
    this.element.addEventListener('quick-access:open', this.boundHandleQuickAccessOpen)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleGlobalKeydown)
    this.element.removeEventListener('quick-access:open', this.boundHandleQuickAccessOpen)
  }

  handleQuickAccessOpen() {
    this.open()
  }


  handleGlobalKeydown(event) {
    // ALT+Q to open
    if (event.altKey && event.key === 'q') {
      event.preventDefault()
      this.open()
    }
    
    // ESC to close when open
    if (this.isOpen && event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      this.close()
    }
  }

  open() {
    if (this.isOpen) return
    
    this.isOpen = true
    this.containerTarget.classList.remove('hidden')
    this.searchInputTarget.value = ''
    this.search('')
    
    // Add body scroll lock
    document.body.style.overflow = 'hidden'
    
    // Animate modal in
    requestAnimationFrame(() => {
      this.modalTarget.style.transform = 'scale(0.95)'
      this.modalTarget.style.opacity = '0'
      this.backdropTarget.style.opacity = '0'
      
      requestAnimationFrame(() => {
        this.modalTarget.style.transform = 'scale(1)'
        this.modalTarget.style.opacity = '1'
        this.backdropTarget.style.opacity = '1'
        
        // Focus and select all text in search input after animation
        this.searchInputTarget.focus()
        this.searchInputTarget.select()
      })
    })
  }

  close() {
    if (!this.isOpen) return
    
    this.isOpen = false
    this.selectedIndex = -1
    
    // Animate modal out
    this.modalTarget.style.transform = 'scale(0.95)'
    this.modalTarget.style.opacity = '0'
    this.backdropTarget.style.opacity = '0'
    
    setTimeout(() => {
      this.containerTarget.classList.add('hidden')
      // Remove body scroll lock
      document.body.style.overflow = ''
      
      // Reset search
      this.searchInputTarget.value = ''
      this.search('')
      
      // Remove focus from search input
      this.searchInputTarget.blur()
      
      // Dispatch custom event for other controllers
      document.dispatchEvent(new CustomEvent('command-palette:closed'))
    }, 150)
  }

  closeBackground(event) {
    if (this.allowBackgroundCloseValue && event.target === this.containerTarget) {
      this.close()
    }
  }


  search(event) {
    const query = event ? event.target.value.toLowerCase() : ''
    this.selectedIndex = -1
    
    if (query === '') {
      this.showAllSections()
      return
    }
    
    // Hide all sections initially
    this.hideAllSections()
    
    // Filter and show matching items
    this.filteredItems = []
    
    // Search in quick actions
    this.searchInSection(this.quickActionsTarget, query, 'quickActions')
    
    // Search in navigation
    this.searchInSection(this.navigationItemsTarget, query, 'navigation')
    
    // Search in help
    this.searchInSection(this.helpItemsTarget, query, 'help')
    
    // Show results container
    this.resultsTarget.classList.remove('hidden')
  }

  searchInSection(section, query, sectionName) {
    if (!section) return
    
    const items = section.querySelectorAll('[data-command-palette-target="actionItem"]')
    let hasMatches = false
    
    items.forEach((item, index) => {
      const title = item.querySelector('.text-sm.font-medium')?.textContent.toLowerCase() || ''
      const description = item.querySelector('.text-xs.text-gray-500')?.textContent.toLowerCase() || ''
      
      if (title.includes(query) || description.includes(query)) {
        item.classList.remove('hidden')
        this.filteredItems.push({
          element: item,
          section: sectionName,
          index: this.filteredItems.length
        })
        hasMatches = true
      } else {
        item.classList.add('hidden')
      }
    })
    
    // Show section header if there are matches
    if (hasMatches) {
      const sectionHeader = section.previousElementSibling
      if (sectionHeader) {
        sectionHeader.classList.remove('hidden')
        section.classList.remove('hidden')
      }
    }
  }

  showAllSections() {
    // Show all sections
    const sections = [this.quickActionsTarget, this.navigationItemsTarget, this.helpItemsTarget]
    sections.forEach(section => {
      if (section) {
        section.classList.remove('hidden')
        const sectionHeader = section.previousElementSibling
        if (sectionHeader) {
          sectionHeader.classList.remove('hidden')
        }
        
        // Show all items in section
        const items = section.querySelectorAll('[data-command-palette-target="actionItem"]')
        items.forEach(item => item.classList.remove('hidden'))
      }
    })
    
    this.resultsTarget.classList.remove('hidden')
  }

  hideAllSections() {
    const sections = [this.quickActionsTarget, this.navigationItemsTarget, this.helpItemsTarget]
    sections.forEach(section => {
      if (section) {
        section.classList.add('hidden')
        const sectionHeader = section.previousElementSibling
        if (sectionHeader) {
          sectionHeader.classList.add('hidden')
        }
      }
    })
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigateDown()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.navigateUp()
        break
      case 'Enter':
        event.preventDefault()
        this.selectCurrentItem()
        break
      case 'Escape':
        event.preventDefault()
        this.close()
        break
    }
  }

  navigateDown() {
    if (this.filteredItems.length === 0) return
    
    this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredItems.length - 1)
    this.updateSelection()
  }

  navigateUp() {
    if (this.filteredItems.length === 0) return
    
    this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
    this.updateSelection()
  }

  updateSelection() {
    // Remove previous selection
    this.filteredItems.forEach(item => {
      item.element.classList.remove('bg-blue-50', 'text-blue-700')
      item.element.classList.add('hover:bg-gray-50', 'hover:text-gray-700')
    })
    
    // Add selection to current item
    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
      const currentItem = this.filteredItems[this.selectedIndex]
      currentItem.element.classList.add('bg-blue-50', 'text-blue-700')
      currentItem.element.classList.remove('hover:bg-gray-50', 'hover:text-gray-700')
      
      // Scroll into view
      currentItem.element.scrollIntoView({ block: 'nearest' })
    }
  }

  selectCurrentItem() {
    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
      const currentItem = this.filteredItems[this.selectedIndex]
      const link = currentItem.element.querySelector('a')
      if (link) {
        link.click()
      }
    }
  }
}
